/** Lazy-load the Google Maps JavaScript API with Places (New). */

let loadPromise = null;

export function getGoogleMapsApiKey() {
  return String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim();
}

export function isGoogleMapsConfigured() {
  return Boolean(getGoogleMapsApiKey());
}

/**
 * Loads Maps JS and imports the Places library (required for PlaceAutocompleteElement).
 * Uses Google's importLibrary bootstrap so new Places APIs work for post-2025 projects.
 */
export function loadGoogleMaps() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser.'));
  }
  if (loadPromise) return loadPromise;

  const key = getGoogleMapsApiKey();
  if (!key) {
    return Promise.reject(new Error('Missing VITE_GOOGLE_MAPS_API_KEY.'));
  }

  loadPromise = new Promise((resolve, reject) => {
    const finish = async () => {
      try {
        await window.google.maps.importLibrary('places');
        resolve(window.google);
      } catch (err) {
        loadPromise = null;
        reject(err instanceof Error ? err : new Error('Failed to import Places library.'));
      }
    };

    if (window.google?.maps?.importLibrary) {
      finish();
      return;
    }

    // Official Maps bootstrap (supports importLibrary / Places New).
    ((g) => {
      let h;
      let a;
      const p = 'The Google Maps JavaScript API';
      const c = 'google';
      const l = 'importLibrary';
      const q = '__ib__';
      const m = document;
      const b = window;
      b[c] = b[c] || {};
      const d = b[c].maps || (b[c].maps = {});
      const r = new Set();
      const e = new URLSearchParams();
      const u = () =>
        h ||
        (h = new Promise(async (f, n) => {
          a = m.createElement('script');
          e.set('libraries', [...r] + '');
          for (const k in g) {
            e.set(
              k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()),
              g[k]
            );
          }
          e.set('callback', c + '.maps.' + q);
          a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
          d[q] = f;
          a.onerror = () => {
            loadPromise = null;
            n(Error(p + ' could not load.'));
          };
          a.nonce = m.querySelector('script[nonce]')?.nonce || '';
          m.head.append(a);
        }));
      d[l]
        ? console.warn(p + ' only loads once. Ignoring:', g)
        : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
    })({
      key,
      v: 'weekly',
    });

    window.google.maps
      .importLibrary('places')
      .then(() => resolve(window.google))
      .catch((err) => {
        loadPromise = null;
        reject(err instanceof Error ? err : new Error('Failed to load Google Places.'));
      });
  });

  return loadPromise;
}
